import type { RegionalUpdaterGate } from '@/app-shell/flags/desktop-flags';
import {
  getCachedDesktopUpdatesGate,
  getRegionalUpdaterGate,
} from '@/app-shell/flags/desktop-flags';
import { getCachedKindeRegion } from '@/hooks/query/useQueryKindeProperties';
import { type Region } from '@heidi/schema/ml-scribe';
import { check } from '@tauri-apps/plugin-updater';
import {
  updaterBridge,
  UpdaterBridgeError,
  type CachedInstallReport,
  type UpdaterRegion,
} from './index';

/**
 * The resolved updater feed for the current gate + region, and the single source
 * of truth for the compliance-critical routing decision. `blocked` fails closed
 * and offers nothing — never a default channel; `global` is gate OFF, `regional` ON.
 */
export type UpdaterFeed =
  | { kind: 'blocked' }
  | { kind: 'global' }
  | { kind: 'regional'; region: Region };

/**
 * Which of the two delivery paths a check and its install take. Orthogonal to
 * the feed, which decides WHICH channel is read; `cached` prefetches and the
 * prompt waits, `legacy` downloads and installs on click as it always did.
 */
export type UpdateDelivery = 'cached' | 'legacy';

/**
 * The path as an OFFER carries it. `unresolved` is a rehydrated offer, whose
 * stored path says nothing about this session: the restart prompt stays shut
 * for it and this session's first check writes `cached` or `legacy`. An install
 * reads it as `legacy`, the path that needs nothing on disk.
 */
export type OfferedDelivery = UpdateDelivery | 'unresolved';

/**
 * The single fail-closed decision. Pure — takes the gate and region as inputs so
 * both the non-React path ({@link resolveUpdaterFeed}) and React consumers
 * (settings / popover, which read them via hooks) share the exact same logic.
 *
 * `region` MUST come from `kindeProperties.region` — never `getRegion()` /
 * `useRegion()`, which fail open to AU and honour `?region=` (C10).
 */
export function selectUpdaterFeed(
  gate: RegionalUpdaterGate,
  region: Region | undefined
): UpdaterFeed {
  if (gate === 'unknown') return { kind: 'blocked' }; // DC3: provider not ready
  if (gate === 'off') return { kind: 'global' };
  if (!region) return { kind: 'blocked' }; // DC3: ON but region unresolved
  return { kind: 'regional', region };
}

/**
 * Pure form of the delivery decision, so the hook and the non-React path agree.
 * Only a resolved ON reaches the cached path: an unreadable gate answers as the
 * old behaviour, which is the one that cannot be broken by this change.
 */
export function selectUpdateDelivery(gate: 'on' | 'off' | 'unknown'): UpdateDelivery {
  return gate === 'on' ? 'cached' : 'legacy';
}

/**
 * Non-React feed resolution (used by the rematch model). Reads the static gate
 * and the cached region. Returns `{ kind: 'blocked' }` until both resolve.
 */
export function resolveUpdaterFeed(): UpdaterFeed {
  return selectUpdaterFeed(getRegionalUpdaterGate(), getCachedKindeRegion());
}

/** Non-React delivery resolution, read once per check and carried on the offer. */
export function resolveUpdateDelivery(): UpdateDelivery {
  return selectUpdateDelivery(getCachedDesktopUpdatesGate());
}

/**
 * The region a feed binds its native calls to; the global feed passes `null`,
 * which Rust resolves as the configured endpoints with no channel suffix. Both
 * feeds therefore take the same guarded Rust path, for the whole fleet.
 */
function regionFor(feed: Exclude<UpdaterFeed, { kind: 'blocked' }>): UpdaterRegion {
  return feed.kind === 'regional' ? feed.region : null;
}

/**
 * The global feed calls the JS plugin directly on the `legacy` path, so its
 * errors arrive as the plugin's own message rather than through Rust's
 * classifier. These map that text to the same codes, pinned by a fixture.
 */
const GLOBAL_ERROR_PATTERNS: ReadonlyArray<[RegExp, string]> = [
  [/was signed for version .* announced version/, 'UPDATER_SIGNED_VERSION_MISMATCH'],
  [/does not specify the version it was signed for/, 'UPDATER_SIGNED_VERSION_MISSING'],
  [
    /signature verification failed|created with a different key|Invalid encoding in minisign data|Unexpected signature algorithm|signature algorithm is not supported|StreamVerifier only supports|could not be decoded, please check if it is a valid base64|^Invalid (symbol|input length|last symbol|padding)/,
    'UPDATER_SIGNATURE_INVALID',
  ],
  [
    /failed to install the update and to restore the previous app/,
    'UPDATER_PREVIOUS_APP_NOT_RESTORED',
  ],
  [
    /error sending request|request or response body error|error decoding response body|error following redirect|error upgrading connection|HTTP status (client|server) error|operation timed out|Download request failed with status/,
    'UPDATER_NETWORK',
  ],
  [/in the response `platforms` object/, 'UPDATER_TARGET_NOT_FOUND'],
  [/Could not fetch a valid release JSON from the remote/, 'UPDATER_RELEASE_NOT_FOUND'],
  [/^Unsupported (application architecture|OS)/, 'UPDATER_UNSUPPORTED_PLATFORM'],
  [/Authentication failed or was cancelled/, 'UPDATER_INSTALL_PERMISSION_DENIED'],
  [
    /Failed to determine updater package extract path|temp directory is not on the same mount point|failed to create temporary directory|binary for the current target not found in the archive|invalid updater binary format|^Failed to install (\.deb )?package/,
    'UPDATER_INSTALL_IO',
  ],
  [/^Failed to format date$|at line \d+ column \d+/, 'UPDATER_MANIFEST_INVALID'],
  [/Updater does not have any endpoints set/, 'UPDATER_NO_ENDPOINTS'],
  [/must use a secure protocol/, 'UPDATER_INSECURE_ENDPOINT'],
];

/**
 * Text the patterns don't recognise (an OS I/O error, a new plugin message) is
 * coded `UPDATER_UNCLASSIFIED` rather than left blank, so Datadog can count it.
 */
export function classifyGlobalUpdaterError(error: unknown): string {
  const message = error instanceof Error ? error.message : String(error);
  return (
    GLOBAL_ERROR_PATTERNS.find(([pattern]) => pattern.test(message))?.[1] ??
    'UPDATER_UNCLASSIFIED'
  );
}

/** Rethrow a global-feed plugin error as a coded bridge error. */
async function withGlobalErrorCode<T>(run: () => Promise<T>): Promise<T> {
  try {
    return await run();
  } catch (error) {
    if (error instanceof UpdaterBridgeError) throw error;
    const message = error instanceof Error ? error.message : String(error);
    throw new UpdaterBridgeError(message, classifyGlobalUpdaterError(error));
  }
}

/** Whether this call takes the JS plugin rather than Rust. */
function viaPlugin(feed: UpdaterFeed, delivery: UpdateDelivery): boolean {
  return delivery === 'legacy' && feed.kind === 'global';
}

/**
 * Execute a check for the resolved feed. `blocked` resolves to `null` defensively
 * so a mis-routed caller still fails closed; both paths normalise to
 * `{ version, body }`. No deadline here — Rust owns the connection and bounds it.
 */
export async function checkUpdateForFeed(
  feed: UpdaterFeed,
  delivery: UpdateDelivery
): Promise<{ version: string; body?: string } | null> {
  if (feed.kind === 'blocked') return null;
  if (viaPlugin(feed, delivery)) {
    const update = await withGlobalErrorCode(() => check());
    return update ? { version: update.version, body: update.body } : null;
  }
  return updaterBridge.checkUpdateForRegion(regionFor(feed));
}

/**
 * Download and install in one step, which is what the `legacy` path does on
 * click; resolves `true` when one was installed, `false` for `blocked`. Also the
 * path a cached install falls back to when the cache cannot serve the bytes.
 */
export async function installUpdateForFeed(
  feed: UpdaterFeed,
  delivery: UpdateDelivery
): Promise<boolean> {
  if (feed.kind === 'blocked') return false;
  if (viaPlugin(feed, delivery)) {
    return withGlobalErrorCode(async () => {
      const update = await check();
      if (!update) return false;
      await update.downloadAndInstall();
      return true;
    });
  }
  return updaterBridge.installUpdateForRegion(regionFor(feed));
}

/**
 * Record `version` as the newest offer and let Rust own the prefetch; its
 * state arrives on the state event. A no-op for `blocked`, as
 * {@link checkUpdateForFeed} is: a mis-routed caller does no update activity.
 */
export async function recordOfferForFeed(
  feed: UpdaterFeed,
  version: string,
  userInitiated: boolean
): Promise<void> {
  if (feed.kind === 'blocked') return;
  await updaterBridge.prefetchUpdateForRegion(regionFor(feed), version, userInitiated);
}

/**
 * Install the prefetched bundle for `version`. Rust falls back to a full
 * download whenever the cache cannot be trusted, so a cold cache costs the
 * speed and nothing else, and reports which route it took.
 */
export async function installCachedForFeed(
  feed: UpdaterFeed,
  version: string
): Promise<CachedInstallReport> {
  if (feed.kind === 'blocked') return { installed: false, cached: false };
  return updaterBridge.installCachedUpdateForRegion(regionFor(feed), version);
}
